import { useState, type ReactNode } from "react";

import type {
  IconKind,
  ViewControl,
} from "../../shared/api/generated/contracts";
import { useT } from "../../shared/i18n";
import { BottomSheet, Button, Fab, IconButton, Icons } from "../../shared/ui";
import { isCommandAvailable, runCommand } from "./commands";
import type { RendererContext } from "./registry";

/** Header actions shown inline; a third one moves into the overflow sheet. */
const INLINE_HEADER_ACTIONS = 2;

const ICON_GLYPHS: Record<IconKind, (props: { size?: number }) => ReactNode> = {
  plus: Icons.Plus,
  check: Icons.Check,
  calendar: Icons.Calendar,
  clock: Icons.Clock,
  search: Icons.Search,
  info: Icons.Info,
  external_link: Icons.ExternalLink,
};

function ControlIcon({ icon, size }: { icon: IconKind; size: number }) {
  const Glyph = ICON_GLYPHS[icon];
  return <Glyph size={size} />;
}

function availableControls(
  controls: readonly ViewControl[] | undefined,
  context: RendererContext,
): ViewControl[] {
  return (controls ?? []).filter((control) =>
    isCommandAvailable(control.command, context),
  );
}

function HeaderAction({
  control,
  context,
  compact = false,
}: {
  control: ViewControl;
  context: RendererContext;
  /** Beside the tab control: a round icon-only button. */
  compact?: boolean;
}) {
  const run = () => {
    runCommand(control.command, context);
  };
  if (control.icon && (compact || !control.label)) {
    return (
      <IconButton
        label={control.label}
        variant="tinted"
        size="xs"
        className="w-9 px-0"
        onClick={run}
      >
        <ControlIcon icon={control.icon} size={16} />
      </IconButton>
    );
  }
  return (
    <Button variant="tinted" size="xs" onClick={run}>
      {control.icon && <ControlIcon icon={control.icon} size={16} />}
      {control.label}
    </Button>
  );
}

/** Left-aligned pill cluster: at most two inline, the rest under "More". */
export function HeaderActions({
  actions,
  context,
  className = "",
  compact = false,
}: {
  actions: readonly ViewControl[] | undefined;
  context: RendererContext;
  className?: string;
  compact?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const available = availableControls(actions, context);
  if (available.length === 0) return null;
  const inlineLimit = compact ? 1 : INLINE_HEADER_ACTIONS;
  // In compact mode several actions all go behind a single "More" button.
  const inlineCount = compact && available.length > 1 ? 0 : inlineLimit;
  const overflowing = available.length > inlineLimit;
  const inline = overflowing ? available.slice(0, inlineCount) : available;
  const overflow = overflowing ? available.slice(inlineCount) : [];
  return (
    <div
      className={`flex shrink-0 flex-wrap items-center gap-2 ${className}`}
      data-print="hidden"
    >
      {inline.map((control) => (
        <HeaderAction
          key={control.id}
          control={control}
          context={context}
          compact={compact}
        />
      ))}
      {overflow.length > 0 && (
        <>
          <IconButton
            label={t("controls.more")}
            variant="tinted"
            size="xs"
            className="w-9 px-0"
            onClick={() => {
              setOpen(true);
            }}
          >
            <Icons.More size={16} />
          </IconButton>
          <BottomSheet
            open={open}
            onOpenChange={setOpen}
            title={t("controls.more")}
            actions={overflow.map((control) => ({
              id: control.id,
              label: control.label,
              onSelect: () => {
                runCommand(control.command, context);
              },
            }))}
          />
        </>
      )}
    </div>
  );
}

/** The floating action button for the active tab, or the view. */
export function ActiveFab({
  control,
  context,
  aboveActionBar,
}: {
  control: ViewControl | undefined;
  context: RendererContext;
  aboveActionBar?: boolean;
}) {
  if (!control || !isCommandAvailable(control.command, context)) return null;
  return (
    <Fab
      label={control.label}
      aboveActionBar={aboveActionBar}
      onClick={() => {
        runCommand(control.command, context);
      }}
    >
      <ControlIcon icon={control.icon ?? "plus"} size={24} />
    </Fab>
  );
}
